/* eslint-disable */

/** TODO: Currently on this page there are a lot of work around's that has arisen from the data given to the frontend
 *   from the backend. One wish would be to change how the data is sent from the backend, so that the frontend could
 *   focus on handling state rather than the different objects that the backend serves and the different versions of
 *   these objects comes in. To see an example of this, look at some of the getters, they have to handle different
 *   versions of the same objects depending on the page we are on. My main idea here would be to standardize and in
 *   general simplify the data so the markets are easier to work with for the frontend.
 *
 *   TODO: Marcus here, currently in this file we are using a lot of magic integers for selected values, I would
 *    propose to move these into enums of the form const someSelectedValue = { IT_IS_TRUE_TEXT: 1, IT_IS_FALSE_TEXT: 0 }
 *    eg const RELEVANCE = { ALL: 1, ME: 0 }
 *    This is way easier to read when testing relevance.selected === RELEVANCE.ALL or relevance.selected ===
 *    RELEVANCE.ME and it makes it consistent over all of our platforms frontend
 */

import hashSum from 'hash-sum'
import { FILTER_MUTATIONS } from '@/state/mutation-types'
import { FILTER_ACTIONS } from '@/state/action-types'
import { cats, markets, rates, experience } from '@/api/vue-api'
import enums, { RELEVANCE, RELEVANCE_LIST } from '@/utils/enum'
import { deepClone } from '@/utils/tools'

// Method that takes our state object and then filters out regions depending on which market is selected
function filterRegionsOut(state) {
  return state.regions.selected.filter((region) => {
    if (state.regions.list) {
      const markets =
        typeof state.markets.selected === 'string'
          ? [state.markets.selected]
          : state.markets.selected
      for (const market of markets) {
        if (state.regions.list[market] && state.regions.list[market][region] !== undefined) {
          return true
        }
      }
    }
    return false
  })
}

let oldHash = ''
let oldCpy = {}

const filterStore = {
  strict: import.meta.env.VITE_APP_ENV !== 'production',

  state: {
    // A list of markets we are active in, and the currently selected ones we are viewing
    markets: {
      list: null,
      selected: [],
    },

    // A list of categories that we are viewing an the currently selected one we want to filter by.
    categories: {
      list: null,
      selected: null,
    },

    // Textual search, and a boolean to indicate if we are making an api call
    search: {
      term: '',

      // In places where we are using this we should maybe rely on the edit stores values, as it handel's this as well
      searching: false,
    },

    // Here we have a list of regions usually sat under their respective market codes ie {dk: [copenhagen, aarhus ...]}
    // And a list of selected regions
    regions: {
      list: null,
      selected: [],
    },

    // This is specific to type of pay, like a project that has a specific price sat or one where it is a daily or hourly
    // rate, otherwise the job is usually up for bidding
    type: {
      list: {
        '': 'All',
        hasRate: 'rate.hourly.or.daily',
        fixed: 'rate.fixed',
      },
      selected: '',
    },

    // This indicates if we wanna see results that are relevant to me, ie which jobs matched me or if we wanna see
    // everything. Currently it is only used on the job page, but may be extended to the profiles page in the future
    relevance: {
      list: RELEVANCE_LIST,
      // This selected is used by vue figure out which option is selected
      selected: RELEVANCE.ALL,
      // This selected is used by the api to know which value id the selected value has in the backend
      selectedID: 'all',
    },

    // As it states its a sort by switch that can change depending on the options that the component needs
    sortBy: 'desc',

    // The same as the sort by, but for match, currently used on the search bar to switch between matching on all searches
    // or matching broadly on our services
    match: 'broad',

    // This is used to filter between different rate ranges ie 10$ - 50$ or 100$ - 200$,
    // we get a list of rate ranges and select one of them
    rates: {
      list: null,
      selected: null,
    },

    // Here we get a list of experience levels we wanna filter by and can select one of them
    // ie ["student", "junior", "expert", ...]
    experience: {
      list: null,
      selected: null,
    },

    // Since we are dependent sometimes to know which page we are on, we have a page type to inform the filter
    // it is currently not used for any visual effects on the platform, see the types
    pageType: null,

    // This indicates if we are looking at our own owned jobs or freelancers, currently it is only used on the
    // profiles page, for organizations with their own roster of freelancers
    own: false,

    // This handles options regarding jobs or profiles we are engaging with ie, I wanna see all profiles I am in a
    // dialog with. Has multiple options but right now only one selected at a time.
    // Currently only on the profiles page, and currently disabled as of writing this message
    engaged: {
      list: null,
      selected: null,
    },

    // This handel's which sub division we wan't to filter by. Say we are a large organization which has sub companies
    // or sub divisions in different countries, and we want to view the jobs or freelancers from one of these sub
    // divisions. This is the filter option for that
    divisions: {
      list: {},
      selected: '',
    },
  },

  getters: {
    // Simple getter for watching changes in the filter
    filter(state) {
      if (hashSum(state) !== oldHash) {
        oldCpy = deepClone(state)
        oldHash = hashSum(state)
      }
      return oldCpy
    },

    // This is simply an abstraction that also gives us the possibility to count the number of jobs that matches with the
    // user, for the market
    markets: (state, getters, rootState, rootGetters) => {
      if (state.relevance.selected === RELEVANCE.ALL && state.pageType === enums.pages.job) {
        return state.markets
      }
      if (state.markets.list === null) return { list: null, selected: state.markets.selected }

      const markets = {}
      for (const m in state.markets.list) {
        if (state.pageType === enums.pages.job) {
          markets[m] = { counted: 0, name: state.markets.list[m].name }
        } else {
          markets[m] = { name: state.markets.list[m].name }
        }
      }

      if (
        rootGetters.allMatchedJobs &&
        rootGetters.allMatchedJobs.length &&
        state.pageType === enums.pages.job
      ) {
        for (const job of rootGetters.allMatchedJobs) {
          markets[job.market].counted += 1
        }
      }
      if (state.pageType === enums.pages.profiles) {
        return {
          list: markets,
          selected:
            typeof state.markets.selected !== 'string'
              ? state.markets.selected[0]
              : state.markets.selected,
        }
      }
      return {
        list: markets,
        selected: state.markets.selected,
      }
    },

    // This getter splits the regions into different objects that are indexed by market, and are ready to use by
    // the SelectBoxes.vue component.
    marketRegions: (state, getters, rootState, rootGetters) => {
      let regions = { list: {}, selected: state.regions.selected }

      if (state.relevance.selected === RELEVANCE.ALL) {
        // We are currently viewing all jobs, so we return the standard region count, we get from the server
        regions = state.regions
      } else {
        // We now are looking into the individual jobs, and instead of calling the server for an individual count, we
        // simply count the jobs here on the frontend.

        // This looks a bit messy, but we simply copy the state.regions.list into a new object,
        // and reset the counted value
        for (const market in state.markets.list) {
          if (regions.list[market] === undefined) regions.list[market] = {}
          for (const region_id in state.regions.list[market]) {
            if (regions.list[market][region_id] === undefined) {
              if (state.pageType === enums.pages.job) {
                regions.list[market][region_id] = {
                  counted: 0,
                  name: state.regions.list[market][region_id].name,
                }
              } else {
                regions.list[market][region_id] = {
                  name: state.regions.list[market][region_id].name,
                }
              }
            }
          }
        }
        // Here we simply count all the jobs in the different regions
        if (
          rootGetters.allMatchedJobs &&
          rootGetters.allMatchedJobs.length &&
          state.pageType === enums.pages.job
        ) {
          for (const job of rootGetters.allMatchedJobs) {
            if (
              job.region_id !== undefined &&
              regions.list[job.market] &&
              regions.list[job.market][job.region_id] &&
              regions.list[job.market][job.region_id].counted !== undefined
            ) {
              regions.list[job.market][job.region_id].counted += 1
            }
          }
        }
      }

      // Here we create the market regions that will go into the children property for SelectBoxes
      const res = []

      for (const market in state.markets.list) {
        res[market] = {
          refObj: {
            list: regions.list[market],
            selected: regions.selected,
          },
          title: '',
          type: 'checkbox',
          name: 'regions',
          approved: false,
          mutator: FILTER_MUTATIONS.SET_REGIONS_SELECTED,
        }
      }
      return res
    },

    // If we only displaying one market, we give this region list to the regions part of the filter
    singleMarketRegions: (state) => {
      if (state.regions.list) {
        return {
          list: state.regions.list[currentMarket],
          selected: state.regions.selected,
        }
      }
      return {
        list: null,
        selected: state.regions.selected,
      }
    },

    // Takes the category list and converts it from a list with sublist's to an object that is ready to be put into
    // our recursive dropdown selector // See the component SelectBoxes
    categories: (state) => {
      const cats = state.categories.list
      if (cats) {
        // Since some categories are children of others we run through and find those categories, and put them into
        // A child object that is then added to the final object used by the SelectBoxes component
        const children = []
        for (const child of cats.filter((cat) => cat.parent_id !== 0)) {
          const id = child.parent_id
          if (!children[id]) {
            children[id] = {
              refObj: { list: [], selected: state.categories.selected },
              title: '',
              type: 'radio',
              name: 'categories',
              approved: false,
              mutator: FILTER_MUTATIONS.SET_CATS_SELECTED,
            }
          }
          children[id].refObj.list.push(child)
        }

        return {
          // Here we only want the reference object to have the parent categories
          refObj: {
            list: cats.filter((cat) => cat.parent_id === 0),
            selected: state.categories.selected,
          },
          title: $t('Categories'),
          type: 'radio',
          name: 'categories',
          approved: false,
          mutator: FILTER_MUTATIONS.SET_CATS_SELECTED,
          children,
        }
      }

      return {
        refObj: {},
        title: $t('Categories'),
        type: 'radio',
        name: 'categories',
        approved: false,
        mutator: FILTER_MUTATIONS.SET_CATS_SELECTED,
      }
    },

    // Takes the devision object and makes it compatible with our SelectBoxes component
    divisions: (state) =>
      // let divs = state.divisions.list
      ({
        refObj: state.divisions,
        title: $t('Divisions'),
        type: 'checkbox',
        name: 'divisions',
        approved: false,
        mutator: FILTER_MUTATIONS.SET_DIVISION_SELECTED,
        collapsable: true,
        // We currently only want to display the divisions filter if there are any divisions to display, and that
        // the user is a company, we may in the future want to revise this.
        display:
          Object.keys(state.divisions.list).length > 1 && currentAccount.is_company === 1 && false,
      }),

    // Adds a standard value to the experience object otherwise nothing else
    experience: (state) => {
      if (state.experience.list) {
        return {
          list: state.experience.list,
          selected: state.experience.selected,
        }
      }
      return {
        list: null,
        selected: state.experience.selected,
      }
    },

    // This builds an engaged selector object from the global variables currentUserHasDialogs and currentUserHasHires
    engaged: (state) => {
      // 'hired': currentUserHasHires ? $t('All hires') : '',
      // 'contract': $t('Current hires'),
      let options = { likes: $t('Saved') }
      if (currentUserHasDialogs) {
        options = Object.assign(options, { dialog: $t('In conversations') })
      }
      if (currentUserHasHires) {
        options = Object.assign(options, {
          hired: $t('All hires'),
          contract: $t('Current hires'),
        })
      }
      options = Object.assign(options, { '': $t('None') })
      return {
        list: options,
        selected: '',
      }
    },

    // Gets the rates for the currently selected market and formats them into a list with { rateKey: textual_representation }
    rates: (state) => {
      if (state.rates.list && state.markets.selected && state.rates.list[state.markets.selected]) {
        const ratesMarket = state.rates.list[state.markets.selected]
        const rates = { '': $t('All') }
        for (const rate of ratesMarket.list) {
          const v = rate.split(',')

          // Convert the data into the text we display in the options.
          rates[rate] = `${v[0]}${v[1] !== '' ? ` - ${v[1]}` : '+'} ${ratesMarket.currency}`
        }

        return {
          list: rates,
          selected: state.rates.selected,
        }
      }
      return {
        list: null,
        selected: state.rates.selected,
      }
    },

    // For jobs we have different types like hourly and daily, here we return that state object
    jobType: (state) => state.type,

    // We return the relevance objects, but add a counter for the ones relevant to us
    relevance: (state, getters, rootState, rootGetters) => {
      const rel = { ...state.relevance.list }

      // Here we add the counted value to the options
      rel[RELEVANCE.MATCH].counted =
        rootGetters.allMatchedJobs &&
        rootGetters.allMatchedJobs[0] &&
        rootGetters.allMatchedJobs[0]['no-jobs-msg'] === undefined
          ? rootGetters.allMatchedJobs.length
          : 0
      rel[RELEVANCE.CLIENTS].counted =
        rootGetters.allClientsJobs &&
        rootGetters.allClientsJobs[0] &&
        rootGetters.allClientsJobs[0]['no-jobs-msg'] === undefined
          ? rootGetters.allClientsJobs.length
          : 0 // Not implemented
      if (!currentUserHasClients) delete rel[RELEVANCE.CLIENTS] // Remove the option for client selection if they have none

      return {
        list: rel,
        selected: state.relevance.selected,
      }
    },
  },

  actions: {
    // Action that sets up the filter for the job page, by getting all the different filters that we know we need for this page
    [FILTER_ACTIONS.INITIATE_JOBS_FILTER]({ commit }) {
      return new Promise(async (resolve, reject) => {
        try {
          commit(FILTER_MUTATIONS.SET_PAGE_TYPE, enums.pages.job)
          const catsResponse = await cats.getCats()
          const marketsResponse = await markets.get(1) // 1 ensures that we get counted numbers with us

          commit(FILTER_MUTATIONS.SET_MARKETS_LIST, marketsResponse.data.markets)
          commit(FILTER_MUTATIONS.SET_CATS_LIST, catsResponse.data)
          commit(FILTER_MUTATIONS.SET_REGIONS_LIST, marketsResponse.data.regions)
          resolve()
        } catch (e) {
          reject(e)
        }
      })
    },

    // Action that sets up the filter for the profiles page
    [FILTER_ACTIONS.INITIATE_PROFILES_FILTER]({ commit, state }) {
      return new Promise(async (resolve, reject) => {
        try {
          // Merge division names with id's
          const merged = {}
          for (let i = 0; i < currentOrgDivisionsId.length; i++) {
            merged[currentOrgDivisionsId[i]] = currentOrgDivisions[i]
          }

          commit(FILTER_MUTATIONS.SET_PAGE_TYPE, enums.pages.profiles)
          // commit(FILTER_MUTATIONS.MARKET_SELECTED, currentMarket)
          commit(FILTER_MUTATIONS.SET_SORT_BY, 'relevance')

          if (merged[currentAccount.id]) {
            commit(FILTER_MUTATIONS.SET_DIVISION_SELECTED, currentAccount.id)
          }

          commit(FILTER_MUTATIONS.SET_DIVISION_LIST, merged)

          const catsResponse = await cats.getCats()
          const marketsResponse = await markets.get()
          const ratesResponse = await rates.get()
          const experienceResponse = await experience.get()

          commit(FILTER_MUTATIONS.SET_CATS_LIST, catsResponse.data)
          commit(FILTER_MUTATIONS.SET_MARKETS_LIST, marketsResponse.data.markets)
          commit(FILTER_MUTATIONS.SET_REGIONS_LIST, marketsResponse.data.regions)
          commit(FILTER_MUTATIONS.SET_EXPERIENCE_LIST, experienceResponse.data)
          if (
            state.experience.selected.length === 0 ||
            state.experience.selected.length === Object.keys(experienceResponse.data).length
          ) {
            commit(FILTER_MUTATIONS.SET_EXPERIENCE_SELECTED, Object.keys(experienceResponse.data))
          }
          commit(FILTER_MUTATIONS.SET_RATES_LIST, ratesResponse.data)
          commit(FILTER_MUTATIONS.SET_FILTER_SETUP_COMPLETE, true)
          resolve()
        } catch (e) {
          reject(e)
        }
      })
    },

    /**
     * Action that updates selected filter parts from a given query object,
     * Also needs the current user
     */
    [FILTER_ACTIONS.UPDATE_FILTER_FROM_QUERY]({ commit, state }, query) {
      // Set the filter from initial query
      commit(FILTER_MUTATIONS.SET_SEARCH_TERM, query.s)
      commit(FILTER_MUTATIONS.SET_TYPE_SELECT, query.rate_type || '')
      if (query.sort) {
        commit(FILTER_MUTATIONS.SET_SORT_BY, query.sort)
      }
      commit(FILTER_MUTATIONS.SET_RATES_SELECTED, query.rate)
      commit(FILTER_MUTATIONS.SET_EXPERIENCE_SELECTED, query.exp ? query.exp.split('|') : '')
      commit(FILTER_MUTATIONS.SET_MATCH_TYPE, query.match)
      commit(FILTER_MUTATIONS.SET_ENGAGED_SELECTED, query.engaged || '')
      if (query.own) {
        commit(FILTER_MUTATIONS.SET_OWN_SELECTED, true)
      }
      if (query.division) {
        commit(FILTER_MUTATIONS.SET_DIVISION_SELECTED, query.division.split(','))
      }
      // Here we get the categories from the query
      if (query.category_id !== undefined) {
        commit(FILTER_MUTATIONS.SET_CATS_SELECTED, query.category_id)
      } else {
        const qCats = query.cat_id ? query.cat_id.split(',') : []
        const cats = []

        for (const id in qCats) {
          // Since we later like to use a dictionary to lookup categories, we save the key as a key pair
          cats[qCats[id]] = parseInt(qCats[id])
        }

        commit(FILTER_MUTATIONS.SET_CATS_SELECTED, cats)
      }

      // Set the market, and default to users market if no market is specified in the query

      if (query.markets) {
        commit(FILTER_MUTATIONS.MARKET_SELECTED, query.markets.split(','))
      } else if (typeof currentAccount === 'object') {
        const markets = []
        if (currentAccountType === 'company') {
          markets.push(currentMarket || currentAccount.market)
        } else {
          for (const m of currentAccount.markets) {
            markets.push(m.market ? m.market : m)
          }
        }
        commit(FILTER_MUTATIONS.MARKET_SELECTED, markets)
      } else {
        commit(FILTER_MUTATIONS.MARKET_SELECTED, [currentMarket])
      }

      if (query.market) {
        commit(FILTER_MUTATIONS.MARKET_SELECTED, query.market)
      }

      commit(FILTER_MUTATIONS.SET_REGIONS_SELECTED, query.area ? query.area.split(',') : [])

      // We update the relevance filter if there is a user logged in
      if (
        typeof currentAccount === 'object' &&
        currentAccountType !== 'company' &&
        currentAccount.isApproved
      ) {
        // If the query is set, we return that, else we default to show mathing jobs
        commit(
          FILTER_MUTATIONS.SET_RELEVANCE,
          query.relevance
            ? query.relevance
            : currentUserHasClients
            ? RELEVANCE.CLIENTS
            : RELEVANCE.MATCH,
        )
      } else {
        // The user can't be show matching jobs, so we default to show all jobs
        commit(FILTER_MUTATIONS.SET_RELEVANCE, RELEVANCE.ALL)
      }
    },
  },

  mutations: {
    // Market mutators

    // Sets the selected market, since we can have more than one selected at a time, this mutator takes that into
    // consideration
    [FILTER_MUTATIONS.MARKET_SELECTED](state, select) {
      if (state.pageType === enums.pages.profiles) {
        if (typeof select === 'object') {
          // We check if we have an array
          if (select.length > 1) {
            // Since we only allow one selected market at any time for the profiles search page, we take the first option
            state.markets.selected = [select[0]]
          } else {
            state.markets.selected = select
          }
        } else {
          // Since we might get a string on the form "dk,uk,se" we split that up and take the first option
          if (typeof select.split === 'function' && select.split(',').length > 1) {
            state.markets.selected = [select.split(',')[0]]
          } else {
            // We only have one option in the string and therefore can pack that option into an array
            state.markets.selected = [select]
          }
        }
        // Remove any selected regions that don't belong to this market
        state.regions.selected = filterRegionsOut(state)
        return // We have updated the markets and can stop the function
      }
      // Below is if we are not on the profiles page

      // We check if the select is an object, as this could mean we are being sent a copy of ourselves
      // and then we have to update from that
      if (typeof select !== 'object') {
        select = [select]
      }

      // We sort the selected markets to ensure a predictable list of selected markets, since this is used when caching
      if (select.selected !== undefined) {
        state.markets.selected = select.selected.sort()
      } else {
        state.markets.selected = select.sort()
      }

      // This part ensures that we deselect any regions that may have been selected in the current markets if we just
      // deselected a market
      if (state.regions.list != null) {
        const res = filterRegionsOut(state)

        if (res.length !== state.regions.selected.length) {
          state.regions.selected = res
        }
      }
    },

    // Set the market list
    [FILTER_MUTATIONS.SET_MARKETS_LIST](state, list) {
      state.markets.list = list
    },

    // Search mutators
    [FILTER_MUTATIONS.SET_SEARCH_TERM](state, term) {
      // We might get a list of terms ie ["js", "vue", "laravel" ...] but we want our search to be a string, so we convert it here
      if (typeof term === 'object' && term.length !== undefined) {
        term = term.join(',')
      }

      // Sometimes we get a "," at the start of a search, ie when we get a [""] it turns into ",", so we remove the ,
      // so we get an empty string instead
      if (term && term[0] === ',') {
        term = term.slice(1, term.length)
      }

      state.search.term = term
    },

    // Start searching | Should be deprecated and moved to edit-store
    [FILTER_MUTATIONS.SEARCHING](state) {
      state.search.searching = true
    },

    // Stop searching | Should be deprecated and moved to edit-store
    [FILTER_MUTATIONS.FINISHED_SEARCH](state) {
      state.search.searching = false
    },

    // Types mutators
    [FILTER_MUTATIONS.SET_TYPE_SELECT](state, select) {
      // We check if the select is an object, as this could mean we are being sent a copy of ourselves
      // and then we have to update from that
      if (select.selected !== undefined) {
        state.type.selected = select.selected
      } else {
        state.type.selected = select
      }
    },

    // Regions mutators
    [FILTER_MUTATIONS.SET_REGIONS_SELECTED](state, select) {
      // We check if the select is an object, as this could mean we are being sent a copy of ourselves
      // and then we have to update from that
      if (select.selected !== undefined) {
        state.regions.selected = select.selected
      } else {
        state.regions.selected = select
      }

      // This part ensures that we always have the market selected where a selected region is located
      if (state.markets.list != null) {
        const result = []
        const found = {}

        state.regions.selected.filter((region) => {
          for (const market in state.markets.list) {
            if (
              state.markets.list.hasOwnProperty(market) &&
              state.regions.list[market][region] !== undefined &&
              found[market] === undefined
            ) {
              result.push(market)
              found[market] = true
            }
          }
        })

        // Currently on the profiles page, we don't want to be able to select multiple markets at once.
        // Therefore we ensure that only one market is selected at any time here
        if (state.pageType === enums.pages.profiles) {
          for (const m of result) {
            if (m !== state.markets.selected[0]) {
              state.markets.selected = [m]
              state.regions.selected = filterRegionsOut(state)
              break
            }
          }
        } else {
          // We merge the resulting markets that are not already selected and then we filter out any duplicates
          const merged = state.markets.selected.concat(result)
          state.markets.selected = merged.filter((item, pos) => merged.indexOf(item) === pos)
        }
      }
    },

    // Sets the region list
    [FILTER_MUTATIONS.SET_REGIONS_LIST](state, list) {
      state.regions.list = list
    },

    // Sort by mutators
    [FILTER_MUTATIONS.SET_SORT_BY](state, sort) {
      state.sortBy = sort
    },
    [FILTER_MUTATIONS.SET_MATCH_TYPE](state, match) {
      state.match = match || 'broad' // Default is broad TODO: move broad to enum type
    },

    // Categories mutators
    [FILTER_MUTATIONS.SET_CATS_SELECTED](state, cats) {
      state.categories.selected = cats
    },
    [FILTER_MUTATIONS.SET_CATS_LIST](state, cats) {
      state.categories.list = cats
    },

    // Relevance mutators
    [FILTER_MUTATIONS.SET_RELEVANCE](state, select) {
      // We check if the select is an object, as this could mean we are being sent a copy of ourselves
      // and then we have to update from that
      let s = 0
      if (select.selected !== undefined) {
        s = parseInt(select.selected)
      } else {
        s = parseInt(select)
      }

      state.relevance.selected = s
      state.relevance.selectedID = state.relevance.list[s]._id
    },

    // Page type mutators
    [FILTER_MUTATIONS.SET_PAGE_TYPE](state, type) {
      // Just to ensure no unsupported type is selected by accident
      if (enums.pages.types.includes(type)) {
        state.pageType = type
      } else {
        state.pageType = null
      }
    },

    // Rates mutators
    [FILTER_MUTATIONS.SET_RATES_LIST](state, list) {
      state.rates.list = list
    },
    [FILTER_MUTATIONS.SET_RATES_SELECTED](state, selected) {
      state.rates.selected = selected === undefined ? '' : selected
    },

    // Experience mutators
    [FILTER_MUTATIONS.SET_EXPERIENCE_LIST](state, list) {
      state.experience.list = list
    },
    [FILTER_MUTATIONS.SET_EXPERIENCE_SELECTED](state, selected) {
      state.experience.selected =
        !selected || (selected[0] === '' && selected.length === 1) ? [] : selected
    },

    [FILTER_MUTATIONS.SET_OWN_SELECTED](state, bool) {
      // Parses any fancy pant values that are not a boolean type
      switch (typeof bool) {
        case 'undefined':
          bool = false
          break
        case 'string':
          bool = parseInt(bool) > 0
          break
        case 'number':
          bool = bool > 0
          break
        case 'object':
          bool = false
          break
      }
      state.own = bool
    },

    [FILTER_MUTATIONS.SET_ENGAGED_LIST](state, list) {
      state.engaged.list = list
    },

    [FILTER_MUTATIONS.SET_ENGAGED_SELECTED](state, selected) {
      state.engaged.selected = selected === undefined ? '' : selected
    },

    [FILTER_MUTATIONS.SET_DIVISION_LIST](state, list) {
      state.divisions.list = list
    },

    [FILTER_MUTATIONS.SET_DIVISION_SELECTED](state, selected) {
      state.divisions.selected = selected === undefined ? '' : selected
    },

    [FILTER_MUTATIONS.SET_FILTER_SETUP_COMPLETE](state, isComplete) {
      state.filterSetupComplete = isComplete
    },
  },
}

export default filterStore
